import React, { useState } from 'react';
import { Card, CardContent, Button, Input, Dialog } from '../ui';
import { useAccounts, useAddAccount, useUpdateAccount, useDeleteAccount, useAccountMonthlyUsage } from '../../hooks';

const LIMIT = 300;

const getProgressColor = (used) => {
  if (used < 200) return 'bg-green-500';
  if (used <= 280) return 'bg-yellow-500';
  return 'bg-red-500';
};

function AccountCard({ account, onEdit, onDelete }) {
  const { data: monthlyUsage = 0 } = useAccountMonthlyUsage(account.id);
  const progress = Math.min(100, (monthlyUsage / LIMIT) * 100);

  return (
    <Card className="mb-3 p-4">
      <div className="font-bold text-base mb-1">
        {account.person_name} @ {account.shipping_company_name}
      </div>
      <div className="mb-3">
        <div className="flex justify-between text-xs text-muted-foreground mb-1">
          <span>თვიური ლიმიტი</span>
          <span>{monthlyUsage.toFixed(2)} / {LIMIT} GEL</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full ${getProgressColor(monthlyUsage)} transition-all`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="flex-1 h-11" onClick={() => onEdit(account)}>
          რედაქტირება
        </Button>
        <Button size="sm" variant="destructive" className="flex-1 h-11" onClick={() => onDelete(account.id)}>
          წაშლა
        </Button>
      </div>
    </Card>
  );
}

function AccountForm({ account, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    person_name: account?.person_name || '',
    shipping_company_name: account?.shipping_company_name || '',
    notes: account?.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">სახელი</label>
        <Input
          value={formData.person_name}
          onChange={(e) => setFormData({ ...formData, person_name: e.target.value })}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">სატრანსპორტო კომპანია</label>
        <Input
          value={formData.shipping_company_name}
          onChange={(e) => setFormData({ ...formData, shipping_company_name: e.target.value })}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">შენიშვნა</label>
        <Input
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" className="flex-1 h-11">
          შენახვა
        </Button>
        <Button type="button" variant="outline" className="flex-1 h-11" onClick={onCancel}>
          გაუქმება
        </Button>
      </div>
    </form>
  );
}

export default function AccountsManager() {
  const { data: accounts = [], isLoading } = useAccounts();
  const addAccount = useAddAccount();
  const updateAccount = useUpdateAccount();
  const deleteAccount = useDeleteAccount();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  const handleAdd = () => {
    setEditingAccount(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setIsDialogOpen(true);
  };

  const handleDelete = (id) => {
    if (confirm('დაადასტურეთ წაშლა')) {
      deleteAccount.mutate(id);
    }
  };

  const handleSave = (formData) => {
    if (editingAccount) {
      updateAccount.mutate({ id: editingAccount.id, data: formData });
    } else {
      addAccount.mutate(formData);
    }
    setIsDialogOpen(false);
  };

  if (isLoading) {
    return (
      <div className="space-y-3 pb-20">
        <div className="flex justify-between items-center mb-4">
          <div className="h-7 w-32 bg-muted rounded-lg animate-pulse" />
          <div className="h-11 w-28 bg-muted rounded-lg animate-pulse" />
        </div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-32 w-full bg-muted rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="pb-20">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">აქაუნთები</h2>
        <Button onClick={handleAdd} className="h-11">
          + დამატება
        </Button>
      </div>

      {accounts.length === 0 ? (
        <div className="text-center py-16 animate-fade-in">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-indigo-200/40 dark:bg-indigo-500/20 rounded-full blur-3xl" style={{ width: '100px', height: '100px', margin: '0 auto' }} />
            <div className="relative text-5xl mb-2">📋</div>
          </div>
          <h3 className="text-lg font-bold text-foreground mb-2">აქაუნთები ჯერ არ არის</h3>
          <p className="text-muted-foreground text-sm">დაამატე აქაუნთი + ღილაკით</p>
        </div>
      ) : (
        accounts.map((account) => (
          <AccountCard
            key={account.id}
            account={account}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ))
      )}

      <Dialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={editingAccount ? 'აქაუნთის რედაქტირება' : 'აქაუნთის დამატება'}
      >
        <AccountForm
          account={editingAccount}
          onSave={handleSave}
          onCancel={() => setIsDialogOpen(false)}
        />
      </Dialog>
    </div>
  );
}
